import { JSX } from "react";
import { Page, View, Document } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { FontSizeProvider } from "components/Resume/ResumePDF/FontSizeContext";
import { Profile } from "./Profile";
import { Summary } from "./Symmary";
import { Skills } from "./Skills";
import { Experience } from "./Experience";
import { Education } from "./Education";
import { Project } from "./Project";
import { Certificate } from "./Certificate";
import { Language } from "./Language";
import { Award } from "./Award";
import { Custom } from "./Custom";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { A4_HEIGHT_PX, LETTER_HEIGHT_PX } from "lib/constants";

export const TemplateThree = ({
  resume,
  settings,
  isPDF = false,
  enableHighlight = false,
  enableMistakeHighlight = false,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    certifications,
    languages,
    awards,
    skills,
    custom,
  } = resume;
  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
    showBulletPoints,
  } = settings;
  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <Experience
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    educations: () => (
      <Education
        heading={formToHeading["educations"]}
        educations={educations}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    projects: () => (
      <Project
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
        enableHighlight={enableHighlight}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    certifications: () => (
      <Certificate
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
        enableMistakeHighlight={enableMistakeHighlight}
      />
    ),
    languages: () => (
      <Language heading={formToHeading["languages"]} languages={languages} enableMistakeHighlight={enableMistakeHighlight} />
    ),
    awards: () => <Award heading={formToHeading["awards"]} awards={awards} enableMistakeHighlight={enableMistakeHighlight} />,
    skills: () => <Skills heading={formToHeading["skills"]} skills={skills} />,
    custom: () => <Custom heading={formToHeading["custom"]} custom={custom} enableMistakeHighlight={enableMistakeHighlight} />,
  };

  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            fontSize: fontSize + "pt",
            height: documentSize === "A4" ? A4_HEIGHT_PX : LETTER_HEIGHT_PX,
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            <View
              style={{
                paddingTop: `${spacing[5]}`,
                backgroundColor: themeColor,
              }}
              fixed={true}
            ></View>
            <Profile profile={profile} themeColor={themeColor} isPDF={isPDF} enableHighlight={enableHighlight} />
            <View
              style={{
                ...styles.flexCol,
                padding: `${spacing[0]} ${spacing[6]}`, // 10 -> 6
              }}
            >
              {profile.summary && <Summary profile={profile} heading="SUMMARY" enableHighlight={enableHighlight} enableMistakeHighlight={enableMistakeHighlight} />}
              {showFormsOrder.map((form) => {
                const Component = formTypeToComponent[form];
                return <Component key={form} />;
              })}
            </View>
            <View
              style={{
                paddingBottom: `${spacing[5]}`,
              }}
              fixed
            ></View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
